/**
 * Where the codex is opened from, and at what (U6).
 *
 * `WorldPanel`'s open state was local `useState`, which is the right answer right up until
 * something outside the panel has to open it — and U6's whole point is that a word in the
 * middle of a chapter, four components away, opens Mira's card. Threading a setter from
 * `PlayRoute` down through `StoryFeed` → `ChapterScene` → `Prose` → one run of one paragraph
 * would put five components in the business of a drawer's open state.
 *
 * So it is a module-level store, shaped like `features/feedback/store.ts` for the same reason
 * that one is: the caller does not know a panel exists. What is different here is that the
 * target is *optional* — the `w` shortcut and the toggle button open the panel at nothing —
 * so unlike the feedback store's `context`, the target cannot also be the open flag.
 *
 * **`opens` is `features/turn/draft.ts`'s counter trick, and it is load-bearing for the same
 * reason.** Tapping the same name twice has to scroll to that card twice; an effect keyed on
 * the target alone gets that wrong by construction, because the second tap changes no value
 * at all.
 */
import { create } from 'zustand';
import type { MentionKind } from '@/api/generated';

export interface CodexTarget {
  kind: MentionKind;
  key: string;
}

interface CodexStore {
  isOpen: boolean;
  /** What to scroll to and expand. Null for an untargeted open — the shortcut and the button. */
  target: CodexTarget | null;
  /** Incremented by every `open()`. The panel keys its scroll effect on this, not on `target`. */
  opens: number;
  // Declared as properties rather than methods on purpose: components pull them out of the
  // store with a selector, and `@typescript-eslint/unbound-method` is right to object to a
  // method shorthand being separated from its object.
  open: (target?: CodexTarget) => void;
  close: () => void;
  /** The `w` shortcut and the visible toggle: open at nothing, or shut. */
  toggle: () => void;
}

export const useCodexStore = create<CodexStore>()((set, get) => ({
  isOpen: false,
  target: null,
  opens: 0,
  open: (target) =>
    set((state) => ({ isOpen: true, target: target ?? null, opens: state.opens + 1 })),
  // The target is cleared on close so the next untargeted open does not re-scroll to
  // whatever the last link pointed at.
  close: () => set({ isOpen: false, target: null }),
  toggle: () => (get().isOpen ? get().close() : get().open()),
}));

/** Callable from an event handler anywhere — a mention run, the shortcut, the toggle. */
export function openCodex(target?: CodexTarget): void {
  useCodexStore.getState().open(target);
}
